import _ from 'lodash';
import {
  ToolConfig,
  ToolConfigDataAccess,
  ToolConfigDataTypeAccess,
  ToolConfigType,
} from '../interfaces/toolConfig';
import { OrgEnvironmentType } from '../enums/organization';

const TYPE_HIERARCHY = {
  [ToolConfigType.System]: 1,
  [ToolConfigType.UserEnvironment]: 2,
  [ToolConfigType.UserOrganization]: 3,
};

const FUNCTION_ACCESS_TYPE_HIERARCHY = {
  [ToolConfigDataTypeAccess.None]: 1,
  [ToolConfigDataTypeAccess.Read]: 2,
  [ToolConfigDataTypeAccess.Write]: 3,
};

interface CalculateDataAccessParams {
  toolConfigs: ToolConfig[];
  sfOrgEnvironment: OrgEnvironmentType;
  cirraOrganizationId?: string | null | undefined;
  cirraUserId?: string | null | undefined;
  sfOrgId?: string | null | undefined;
}

interface GetRelevantToolConfigsOutput {
  systemConfig: ToolConfig | null;
  orgTypeConfig: ToolConfig | null;
  individualOrgConfig: ToolConfig | null;
}

const _getRelevantToolConfigs = ({
  toolConfigs,
  sfOrgEnvironment,
  cirraUserId,
  cirraOrganizationId: _cirraOrganizationId,
  sfOrgId,
}: CalculateDataAccessParams): GetRelevantToolConfigsOutput => {
  const cirraOrganizationId = _cirraOrganizationId || null;

  const _isConfigForEnv = (cfg: ToolConfig) => {
    return cfg.salesforce_environment === sfOrgEnvironment;
  };

  let individualOrgConfig = null;
  if (sfOrgId) {
    individualOrgConfig = toolConfigs.find(
      (cfg) =>
        cfg.type === ToolConfigType.UserOrganization &&
        cfg.salesforce_organization_id === sfOrgId &&
        cfg.cirra_user_id === cirraUserId &&
        cfg.cirra_organization_id === cirraOrganizationId
    );
  }

  // Find the configuration for the organization type
  const orgTypeConfig = toolConfigs.find(
    (cfg) =>
      cfg.type === ToolConfigType.UserEnvironment &&
      _isConfigForEnv(cfg) &&
      cfg.salesforce_organization_id === null &&
      // configs for team's SF environments always have null cirra_user_id because they are shared across the team users
      // configs for personal user's SF environments have cirra_user_id set to the user's id
      cfg.cirra_user_id === (cirraOrganizationId ? null : cirraUserId) &&
      cfg.cirra_organization_id === cirraOrganizationId
  );

  const systemConfig = toolConfigs.find(
    (cfg) => cfg.type === ToolConfigType.System && _isConfigForEnv(cfg)
  );

  // TODO Give the return properties clearer names
  return {
    systemConfig: systemConfig || null,
    orgTypeConfig: orgTypeConfig || null,
    individualOrgConfig: individualOrgConfig || null,
  };
};

export const getAppliedToolConfig = ({
  toolConfigs,
  sfOrgEnvironment,
  cirraUserId,
  cirraOrganizationId,
  sfOrgId,
}: CalculateDataAccessParams): ToolConfig | null => {
  const { systemConfig, orgTypeConfig, individualOrgConfig } =
    _getRelevantToolConfigs({
      toolConfigs,
      sfOrgEnvironment,
      cirraUserId,
      cirraOrganizationId,
      sfOrgId,
    });
  return individualOrgConfig || orgTypeConfig || systemConfig || null;
};

export const getFallbackToolConfig = ({
  toolConfigs,
  sfOrgEnvironment,
  cirraUserId,
  cirraOrganizationId,
  sfOrgId,
}: CalculateDataAccessParams): ToolConfig | null => {
  const { systemConfig, orgTypeConfig } = _getRelevantToolConfigs({
    toolConfigs,
    sfOrgEnvironment,
    cirraUserId,
    cirraOrganizationId,
    sfOrgId,
  });
  if (sfOrgId) {
    return orgTypeConfig || systemConfig || null;
  }
  return systemConfig || null;
};

export const quantitizeToolConfigType = (type: ToolConfigType) => {
  return TYPE_HIERARCHY[type] || 99;
};

export const quantitizeAccessType = (accessType: ToolConfigDataTypeAccess) => {
  return (
    FUNCTION_ACCESS_TYPE_HIERARCHY[accessType] ||
    FUNCTION_ACCESS_TYPE_HIERARCHY[ToolConfigDataTypeAccess.None]
  );
};

interface GetParentToolConfigsParams {
  toolConfigs: ToolConfig[];
  type: ToolConfigType;
  sfOrgEnvironment: OrgEnvironmentType;
}

/*
 * Sorts tool configs by priority
 * @param {Object[]} toolConfigs
 * @param {boolean} desc - Whether to sort in descending order (the most relevant config first)
 * @returns {Object[]}
 */
const sortToolConfigsByPriority = (toolConfigs: ToolConfig[], desc = false) => {
  const sortedToolConfigs = _.sortBy(toolConfigs, [
    (cfg) => quantitizeToolConfigType(cfg.type),
  ]);
  return desc ? sortedToolConfigs.reverse() : sortedToolConfigs;
};

export const getParentToolConfigs = ({
  toolConfigs,
  type,
  sfOrgEnvironment,
}: GetParentToolConfigsParams) => {
  const cfgs = toolConfigs.filter((cfg) => {
    return (
      cfg.salesforce_environment === sfOrgEnvironment &&
      quantitizeToolConfigType(cfg.type) < quantitizeToolConfigType(type)
    );
  });
  return sortToolConfigsByPriority(cfgs, true);
};

export const calculateToolConfigDataAccess = (toolConfigs: ToolConfig[]) => {
  if (!toolConfigs?.length || toolConfigs.length === 0) {
    return {
      metadata: ToolConfigDataTypeAccess.None,
      personal_setup_data: ToolConfigDataTypeAccess.None,
      user_data: ToolConfigDataTypeAccess.None,
      business_data: ToolConfigDataTypeAccess.None,
    };
  }

  // Sort the tool configs by priority: System > User org environment > User org
  const sortedToolConfigs = sortToolConfigsByPriority(toolConfigs, false);

  // Merge data access from other items in sortedToolConfigs one by one
  const accumulatedDataAccess: ToolConfigDataAccess = {
    ...sortedToolConfigs[0].data_access,
  };
  for (let cfgIdx = 1; cfgIdx < sortedToolConfigs.length; cfgIdx++) {
    const isParentConfigOverridable =
      sortedToolConfigs[cfgIdx - 1].is_overridable;
    const currentConfig = sortedToolConfigs[cfgIdx];

    // Iterate through each data type in data_access
    for (
      let dataTypeIdx = 0;
      dataTypeIdx < Object.keys(accumulatedDataAccess).length;
      dataTypeIdx++
    ) {
      const dataType = Object.keys(accumulatedDataAccess)[
        dataTypeIdx
      ] as keyof ToolConfigDataAccess;
      const accumulatedAccess = accumulatedDataAccess[dataType];
      const currentAccess = currentConfig.data_access[dataType];

      // If the parent config is overridable, allow any properties from the current item's data_access
      // If the parent config is not overridable, allow ONLY STRICTER properties
      if (
        isParentConfigOverridable ||
        quantitizeAccessType(currentAccess) <
          quantitizeAccessType(accumulatedAccess)
      ) {
        accumulatedDataAccess[dataType] = currentAccess;
      }
    }
  }

  return accumulatedDataAccess;
};

const _DATA_ACCESS__LEVEL_NAME = {
  [ToolConfigDataTypeAccess.None]: 'None',
  [ToolConfigDataTypeAccess.Read]: 'Read Only',
  [ToolConfigDataTypeAccess.Write]: 'Read and Write',
};

export const getAccessLevelName = (accessLevel: ToolConfigDataTypeAccess) => {
  return _DATA_ACCESS__LEVEL_NAME[accessLevel] || 'Unknown';
};

interface CheckOptionRestrictionParams {
  level: ToolConfigDataTypeAccess;
  dataType: keyof ToolConfigDataAccess;
  parentToolConfigs: ToolConfig[];
}

export const isOptionRestricted = ({
  level,
  dataType,
  parentToolConfigs,
}: CheckOptionRestrictionParams): boolean => {
  return parentToolConfigs.some((parentConfig) => {
    const isParentNotOverridable = parentConfig.is_overridable !== true;
    const parentDataAccess = parentConfig.data_access[dataType];
    const isLevelTooPermissive =
      quantitizeAccessType(level) > quantitizeAccessType(parentDataAccess);
    return isParentNotOverridable && isLevelTooPermissive;
  });
};

export const getRestrictionTooltip = ({
  dataType,
  parentDataAccess,
  envType,
}: {
  dataType: keyof ToolConfigDataAccess;
  parentDataAccess: ToolConfigDataAccess;
  envType: OrgEnvironmentType;
}): string => {
  const parentalAccessLevel = parentDataAccess[dataType];
  const dataTypeLabels = {
    metadata: 'Metadata',
    user_data: 'User Data',
    personal_setup_data: 'Personal Setup Data',
    business_data: 'Business Data',
  };

  return `Access to ${dataTypeLabels[dataType]} is restricted to ${getAccessLevelName(parentalAccessLevel)} for ${envType} orgs`;
};
